import apiClient from './client';

// ===== BRANCHES =====

/**
 * Get branch mode status (single vs multi-branch)
 */
export const getBranchMode = async () => {
  const response = await apiClient.get('/branches/mode');
  return response.data;
};

/**
 * Get all branches with filters and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20)
 * @param {string} params.search - Search query
 * @param {boolean} params.isActive - Filter by active status
 * @param {string} params.sortBy - Sort field (default: created_at)
 * @param {string} params.sortOrder - Sort order: ASC or DESC (default: DESC)
 */
export const getBranches = async (params = {}) => {
  const response = await apiClient.get('/branches', { params });
  return response.data;
};

/**
 * The Branches page: totals and every branch's figures for a period
 * @param {Object} params - start_date and end_date (YYYY-MM-DD), or all_time: 1
 * @returns {Promise} { success, data: { period, trend_unit, single, totals, branches, closed } }
 */
export const getBranchesOverview = async (params = {}) => {
  const response = await apiClient.get('/branches/overview', { params });
  return response.data;
};

/**
 * A branch's page header: details, figures for a period and what needs attention there
 * @param {number|string} id - Branch id
 * @param {Object} params - start_date and end_date (YYYY-MM-DD), or all_time: 1
 * @returns {Promise} { success, data: { period, branch, figures, attention } }
 */
export const getBranchSummary = async (id, params = {}) => {
  const response = await apiClient.get(`/branches/${id}/summary`, { params });
  return response.data;
};

/**
 * A branch's Stock tab: its products (stock, reorder level, price, availability) and transfers
 * @param {number|string} id - Branch id
 * @param {Object} params - search, filter ('all' | 'low' | 'out'), page
 * @returns {Promise} { success, data: { counts, stock_value?, items, transfers, page, page_size } }
 */
export const getBranchStock = async (id, params = {}) => {
  const response = await apiClient.get(`/branches/${id}/stock`, { params });
  return response.data;
};

/**
 * A branch's Staff tab: who works there and their sales in a period
 * @param {number|string} id - Branch id
 * @param {Object} params - start_date and end_date (YYYY-MM-DD), or all_time: 1
 * @returns {Promise} { success, data: { period, people } }
 */
export const getBranchPeople = async (id, params = {}) => {
  const response = await apiClient.get(`/branches/${id}/people`, { params });
  return response.data;
};

/**
 * Get single branch by ID
 */
export const getBranchById = async (id) => {
  const response = await apiClient.get(`/branches/${id}`);
  return response.data;
};

// Alias for convenience
export const getBranch = getBranchById;

/**
 * Create new branch
 * @param {Object} data - Branch data
 * @param {string} data.name - Branch name (required)
 * @param {string} data.code - Branch code (optional, auto-generated)
 * @param {string} data.address - Branch address
 * @param {string} data.phone - Branch phone
 * @param {string} data.email - Branch email
 * @param {number} data.managerId - Manager user ID
 * @param {boolean} data.isMain - Is main/HQ branch
 */
export const createBranch = async (data) => {
  const response = await apiClient.post('/branches', data);
  return response.data;
};

/**
 * Update branch
 */
export const updateBranch = async (id, data) => {
  const response = await apiClient.put(`/branches/${id}`, data);
  return response.data;
};

/**
 * Delete branch (soft delete)
 * @param {number} id - Branch ID
 * @param {number|null} targetBranchId - Branch to transfer stock to (optional)
 */
export const deleteBranch = async (id, targetBranchId = null) => {
  const response = await apiClient.delete(`/branches/${id}`, {
    data: { targetBranchId }
  });
  return response.data;
};

/**
 * Set branch as main (exclusive - only one main at a time)
 * @param {number} id - Branch ID to set as main
 */
export const setMainBranch = async (id) => {
  const response = await apiClient.put(`/branches/${id}/set-main`);
  return response.data;
};

// ===== BRANCH PRODUCTS =====

/**
 * Get products in a branch
 * @param {number} branchId - Branch ID
 * @param {Object} params - Query parameters
 */
export const getBranchProducts = async (branchId, params = {}) => {
  const response = await apiClient.get(`/branches/${branchId}/products`, { params });
  return response.data;
};

/**
 * Assign products to a branch
 * @param {number} branchId - Branch ID
 * @param {number[]} productIds - Array of product IDs to assign
 * @param {number} localPrice - Optional branch-specific price
 */
export const assignProductsToBranch = async (branchId, productIds, localPrice = null) => {
  const response = await apiClient.post(`/branches/${branchId}/products`, {
    productIds,
    localPrice
  });
  return response.data;
};

/**
 * Remove product from branch
 */
export const removeProductFromBranch = async (branchId, productId) => {
  const response = await apiClient.delete(`/branches/${branchId}/products/${productId}`);
  return response.data;
};

/**
 * Update product availability in branch
 */
export const updateProductAvailability = async (branchId, productId, isAvailable) => {
  const response = await apiClient.patch(
    `/branches/${branchId}/products/${productId}/availability`,
    {
      isAvailable
    }
  );
  return response.data;
};

/**
 * Update branch-specific price for a product
 */
export const updateBranchProductPrice = async (branchId, productId, localPrice) => {
  const response = await apiClient.patch(`/branches/${branchId}/products/${productId}/price`, {
    localPrice
  });
  return response.data;
};

// ===== BRANCH STAFF =====

/**
 * Get staff in a branch
 * @param {number} branchId - Branch ID
 * @param {Object} params - Query parameters
 */
export const getBranchStaff = async (branchId, params = {}) => {
  const response = await apiClient.get(`/branches/${branchId}/staff`, { params });
  return response.data;
};

/**
 * Transfer staff to a branch
 * @param {number} toBranchId - Target branch ID
 * @param {number} userId - User ID to transfer
 * @param {string} reason - Reason for transfer
 */
export const transferStaff = async (toBranchId, userId, reason = 'Staff transfer') => {
  const response = await apiClient.post(`/branches/${toBranchId}/transfer-staff`, {
    userId,
    reason
  });
  return response.data;
};

/**
 * Get transfer history for a user
 */
export const getTransferHistory = async (userId) => {
  const response = await apiClient.get(`/branches/transfers/${userId}`);
  return response.data;
};

export default {
  // Mode
  getBranchMode,
  // Branches CRUD
  getBranches,
  getBranchById,
  createBranch,
  updateBranch,
  deleteBranch,
  setMainBranch,
  // Products
  getBranchProducts,
  assignProductsToBranch,
  removeProductFromBranch,
  updateProductAvailability,
  updateBranchProductPrice,
  // Staff
  getBranchStaff,
  transferStaff,
  getTransferHistory
};
